import { Power1 } from 'gsap';
import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import { NONE } from '../../constants/DropShadow';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getAlignX,
  getFixAboveY,
  getFixBelowY,
  getFixTopY,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION_PER_LINE = 1;

export default class Stacked extends DraggablePreset {
  constructor(app) {
    super(app);

    /**
     * @type {Text[]}
     */
    this.texts = [];

    /**
     * @type {GraphicMask[]}
     */
    this.masks = [];

    for (let x = 0; x < 4; x++) {
      const text = new Text(app);
      const mask = new GraphicMask();
      text.mask = mask.mask;

      this.texts.push(text);
      this.masks.push(mask);
    }

    this.container.addChild(
      ...this.texts,
      ...this.masks.map((mask) => mask.mask)
    );
  }

  update(model) {
    model = { ...model, dropShadow: NONE }; // disable drop shadow for this preset
    super.update(model);

    this.totalDuration = Math.min(
      MAX_DURATION_PER_LINE * this.numTextLines,
      this.duration
    );
    this.animationDuration = this.totalDuration / (2 * this.numTextLines);

    // split up lines of text
    this.texts[0].text = this.getTextLine(0);
    this.texts[1].text = this.getTextLine(1);
    this.texts[2].text = this.getTextLine(2); // this is the key line
    this.texts[3].text = this.getTextLine(3);

    // style the text
    const textStyle = {
      fontWeight: 'bold',
    };

    this.texts[0].updateStyle(model, {
      ...textStyle,
      scale: 0.5,
    });

    this.texts[1].updateStyle(model, textStyle);

    this.texts[2].updateStyle(model, {
      ...textStyle,
      fill: this.secondaryColor,
    });

    this.texts[3].updateStyle(model, {
      ...textStyle,
      scale: 0.7,
    });

    const texts = this.texts;

    // calculate the height but ignore empty text
    const height = texts.reduce(
      (val, text) => val + (text.text ? text.innerControls.height : 0),
      0
    );
    const width = Math.max(...texts.map((text) => text.innerControls.width));

    this.setDraggableRoot(height, width);

    // align the text
    texts.forEach((_, i) => {
      texts[i].innerControls.x = getAlignX(texts[i].innerControls, this.root);
    });

    // calculate target text positions
    this.texts[0].innerControls.y = getFixTopY(
      this.texts[0].innerControls,
      this.root
    );
    this.texts[1].innerControls.y = getFixBelowY(
      this.texts[1].innerControls,
      this.texts[0].innerControls
    );
    this.texts[2].innerControls.y = getFixBelowY(
      this.texts[2].innerControls,
      this.texts[1].innerControls
    );
    this.texts[3].innerControls.y = getFixBelowY(
      this.texts[3].innerControls,
      this.texts[2].innerControls
    );

    // mask the text in place
    texts.forEach((text, index) => {
      this.setMaskOnText(text, this.masks[index]);
    });

    // initial values to make sure animation works on startup
    this.texts[0].innerControls.y = getFixBelowY(
      this.texts[0].innerControls,
      this.texts[0].innerControls
    );
    this.texts[1].innerControls.y = getFixAboveY(
      this.texts[1].innerControls,
      this.texts[1].innerControls
    );
    this.texts[2].innerControls.y = getFixBelowY(
      this.texts[2].innerControls,
      this.texts[2].innerControls
    );
    this.texts[3].innerControls.y = getFixAboveY(
      this.texts[3].innerControls,
      this.texts[3].innerControls
    );

    // alternate sliding text up and down
    this.upAndDown(this.texts[0].innerControls, 0);
    this.upAndDown(this.texts[1].innerControls, this.animationDuration, true);
    this.upAndDown(this.texts[2].innerControls, this.animationDuration * 2);
    this.upAndDown(
      this.texts[3].innerControls,
      this.animationDuration * 3,
      true
    );
  }

  above(item) {
    return {
      y: getFixAboveY(item, item),
      duration: this.animationDuration,
      ease: Power1.easeOut,
    };
  }

  below(item) {
    return {
      y: getFixBelowY(item, item),
      duration: this.animationDuration,
      ease: Power1.easeOut,
    };
  }

  upAndDown(item, start, reverse = false) {
    const up = this.above(item);
    const down = this.below(item);

    this.animate(item)
      .to(reverse ? down : up, start)
      .reverseOut(this.duration - this.totalDuration / 2 + start);
  }

  render() {
    this.masks.forEach((mask) => mask.render());
  }
}
